export type UserRole = "PROVIDER" | "patient" | "admin";

/**
 * Get storage key with role prefix
 */
export const getStorageKey = (role: UserRole, suffix: string): string => {
  return `vivo_${role}_${suffix}`;
};

/**
 * Get token from role-specific storage
 */
export const getRoleSpecificToken = (role: UserRole): string | null => {
  return localStorage.getItem(getStorageKey(role, "token"));
};

/**
 * Get user ID from role-specific storage
 */
export const getRoleSpecificId = (role: UserRole): string | null => {
  return localStorage.getItem(getStorageKey(role, "id"));
};

/**
 * Get username from role-specific storage
 */
export const getRoleSpecificUsername = (role: UserRole): string | null => {
  return localStorage.getItem(getStorageKey(role, "username"));
};

/**
 * Detect current role based on URL path
 */
export const detectRoleFromUrl = (): UserRole | null => {
  const pathname = globalThis?.location?.pathname ?? "";
  if (pathname.includes("/layout")) return "PROVIDER";
  if (pathname.includes("/user-layout")) return "patient";
  return null;
};

/**
 * Get appropriate token based on context
 * Tries user object first, then falls back to role-based storage
 */
export const getContextAwareToken = (
  userToken: string | undefined,
  role?: UserRole
): string | null => {
  if (userToken) return userToken;

  if (role) {
    return getRoleSpecificToken(role);
  }

  // Try to detect role from URL
  const detectedRole = detectRoleFromUrl();
  if (detectedRole) {
    return getRoleSpecificToken(detectedRole);
  }

  return null;
};

/**
 * Get appropriate ID based on context
 * Tries user object first, then falls back to role-based storage
 */
export const getContextAwareId = (
  userId: string | number | undefined,
  role?: UserRole
): string | null => {
  if (userId) return String(userId);

  if (role) {
    return getRoleSpecificId(role);
  }

  // Try to detect role from URL
  const detectedRole = detectRoleFromUrl();
  if (detectedRole) {
    return getRoleSpecificId(detectedRole);
  }

  return null;
};
